import api from '@/shared/api/api';
import { BASE_URI } from '@/shared/config/config';

import {
  encounterPreviewToDomain,
  encounterToDomain,
} from './scribeEncounterConv';
import { callScribe } from './scribeError';
import {
  TranscriptorApiError,
  transcriptorErrorCode,
} from './transcriptorError';
import type {
  DocumentUpdateRequest,
  RecordingPingTelemetry,
  RecordingStopReason,
  ReprocessRequestOptions,
  TranscriptorApiResponse,
  TranscriptorConsultationResponse,
  TranscriptorEncounterListResponse,
  TranscriptorLanguage,
  UsedEncounterSchema,
} from './transcriptorTypes';

// Recording and upload still speak REST: the contract has no RPCs for the
// pipeline behind them.
const TRANSCRIPTOR_API_BASE = `${BASE_URI}/api/scribe`;

export * from './transcriptorTypes';
export {
  isFollowUpDocument,
  isLiteratureReviewDocument,
} from './scribeEncounterConv';

export {
  SCRIBE_TRANSCRIPTION_DISABLED,
  TRANSCRIPTION_DISABLED_CODE,
  TranscriptorApiError,
} from './transcriptorError';

// API client class
export class TranscriptorApiClient {
  private async request<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<TranscriptorApiResponse<T>> {
    const url = `${TRANSCRIPTOR_API_BASE}${path}`;

    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include', // Include cookies for auth
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          errorMessage = `${errorMessage} - ${errorText}`;
        }

        throw new TranscriptorApiError(
          errorMessage,
          transcriptorErrorCode(response.status, errorMessage),
          undefined,
          response.status,
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof TranscriptorApiError) {
        throw error;
      }
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Unknown error occurred');
    }
  }

  // For endpoints that return data directly without { data, error, meta } wrapper
  private async requestDirect<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${TRANSCRIPTOR_API_BASE}${path}`;

    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          errorMessage = `${errorMessage} - ${errorText}`;
        }

        throw new TranscriptorApiError(
          errorMessage,
          transcriptorErrorCode(response.status, errorMessage),
          undefined,
          response.status,
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Unknown error occurred');
    }
  }

  // Encounter schemas that actually appear in the organization's encounters,
  // with their counts — unlike the catalog listing, a schema nobody used is
  // absent here.
  async getUsedEncounterSchemas(params: {
    organization_id: string;
    include_archived?: boolean;
  }): Promise<UsedEncounterSchema[]> {
    const response = await callScribe(
      api.encounterSchema.listUsage({
        OrganizationID: params.organization_id,
        IncludeArchived: params.include_archived,
      }),
    );

    return response.Schemas.map((schema) => ({
      schema_code: schema.Code,
      schema_name: schema.Name,
      count: schema.Count,
    }));
  }

  async getConsultations(params?: {
    patient_id?: string;
    organization_id?: string;
    schema_code?: string;
    page?: number;
    limit?: number;
    resolve_patient_info?: boolean;
    include_archived?: boolean;
  }): Promise<TranscriptorEncounterListResponse> {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 50;

    const response = await callScribe(
      api.encounter.list({
        PatientID: params?.patient_id,
        OrganizationID: params?.organization_id,
        SchemaCode: params?.schema_code,
        ResolvePatientInfo: params?.resolve_patient_info,
        IncludeArchived: params?.include_archived,
        Offset: (page - 1) * limit,
        Limit: limit,
      }),
    );

    return {
      encounters: response.Encounters.map(encounterPreviewToDomain),
      total: response.Total,
      page,
      limit,
    };
  }

  async getConsultation(
    id: string,
    params?: { resolvePatientInfo?: boolean },
  ): Promise<TranscriptorConsultationResponse> {
    const response = await callScribe(
      api.encounter.get({
        EncounterID: id,
        ResolvePatientInfo: params?.resolvePatientInfo,
      }),
    );

    return encounterToDomain(response.Encounter);
  }

  // Get presigned URL for audio upload
  async getUploadUrl(data: {
    patient_id: string;
    schema_id?: string;
    lang: TranscriptorLanguage;
    file_name: string;
    file_size: number;
    content_type: string;
    external_id?: string;
    selected_on_demand_series_ids?: string[];
  }): Promise<{
    upload_url: string;
    consultation_id: string;
    fragment_id: string;
  }> {
    const schema_id = data.schema_id;

    const response = await this.requestDirect<{
      upload_url: string;
      encounter_id: string;
      fragment_id: string;
    }>('/encounters/upload-url', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: data.patient_id,
        external_id: data.external_id,
        file_name: data.file_name,
        file_size: data.file_size,
        content_type: data.content_type,
        lang: data.lang,
        ...(schema_id ? { schema_id } : {}),
        ...(data.selected_on_demand_series_ids
          ? {
              selected_on_demand_series_ids: data.selected_on_demand_series_ids,
            }
          : {}),
      }),
    });

    return {
      upload_url: response.upload_url,
      consultation_id: response.encounter_id,
      fragment_id: response.fragment_id,
    };
  }

  async getFragmentUploadUrl(
    consultationId: string,
    data: {
      file_name: string;
      file_size: number;
      content_type: string;
    },
  ): Promise<{
    upload_url: string;
    consultation_id: string;
    fragment_id: string;
  }> {
    const response = await this.requestDirect<{
      upload_url: string;
      encounter_id: string;
      fragment_id: string;
    }>(`/encounters/${consultationId}/fragments/upload-url`, {
      method: 'POST',
      body: JSON.stringify({
        file_name: data.file_name,
        file_size: data.file_size,
        content_type: data.content_type,
      }),
    });

    return {
      upload_url: response.upload_url,
      consultation_id: response.encounter_id,
      fragment_id: response.fragment_id,
    };
  }

  // Confirm that file has been uploaded to storage
  async confirmUpload(data: {
    consultation_id: string;
    fragment_id: string;
  }): Promise<{ consultation_id: string; status: string }> {
    const response = await this.requestDirect<{
      encounter_id: string;
      status: string;
    }>('/encounters/confirm-upload', {
      method: 'POST',
      body: JSON.stringify({
        encounter_id: data.consultation_id,
        fragment_id: data.fragment_id,
      }),
    });

    return {
      consultation_id: response.encounter_id,
      status: response.status,
    };
  }

  // Start recording session (browser-style with pre-generated IDs)
  async startRecording(
    consultationId: string,
    fragmentId: string,
    data: {
      patient_id: string;
      schema_id?: string;
      lang: TranscriptorLanguage;
      selected_on_demand_series_ids?: string[];
    },
  ): Promise<TranscriptorApiResponse<{ success: boolean }>> {
    const schema_id = data.schema_id;

    return this.request<{ success: boolean }>(
      `/encounters/${consultationId}/fragments/${fragmentId}/start`,
      {
        method: 'POST',
        body: JSON.stringify({
          patient_id: data.patient_id,
          lang: data.lang,
          ...(schema_id ? { schema_id } : {}),
          ...(data.selected_on_demand_series_ids
            ? {
                selected_on_demand_series_ids:
                  data.selected_on_demand_series_ids,
              }
            : {}),
        }),
      },
    );
  }

  // Add audio chunk
  async addAudioChunk(
    consultationId: string,
    fragmentId: string,
    chunk: {
      audio_base64: string;
      type: string;
      timestamp: string;
      index: number;
      last: boolean;
    },
  ): Promise<TranscriptorApiResponse<{ success: boolean }>> {
    return this.request<{ success: boolean }>(
      `/encounters/${consultationId}/fragments/${fragmentId}/chunks`,
      {
        method: 'POST',
        body: JSON.stringify(chunk),
      },
    );
  }

  // Stop recording. Without a reason the backend records the stop as the user
  // pressing Finish.
  async stopRecording(
    consultationId: string,
    fragmentId: string,
    options?: { reason?: RecordingStopReason },
  ): Promise<TranscriptorApiResponse<{ success: boolean }>> {
    return this.request<{ success: boolean }>(
      `/encounters/${consultationId}/fragments/${fragmentId}/stop`,
      {
        method: 'POST',
        body: options?.reason
          ? JSON.stringify({ reason: options.reason })
          : undefined,
      },
    );
  }

  // Activity ping to prevent server session timeout during recording. The
  // optional telemetry is logged by the backend to diagnose recordings that
  // stop on their own.
  async activityPing(
    consultationId: string,
    fragmentId: string,
    telemetry?: RecordingPingTelemetry,
  ): Promise<TranscriptorApiResponse<{ success: boolean }>> {
    return this.request<{ success: boolean }>(
      `/encounters/${consultationId}/fragments/${fragmentId}/ping`,
      {
        method: 'POST',
        body: telemetry ? JSON.stringify(telemetry) : undefined,
      },
    );
  }

  // Reprocess consultation
  async reprocessConsultation(
    consultationId: string,
    options?: ReprocessRequestOptions,
  ): Promise<TranscriptorApiResponse<Record<string, never>>> {
    return this.request<Record<string, never>>(
      `/encounters/${consultationId}/reprocess`,
      {
        method: 'POST',
        body: JSON.stringify(options || {}),
      },
    );
  }

  async updateConsultation(
    consultationId: string,
    updates: {
      doctor_user_id?: string;
      staff_member_id?: string;
      patient_id?: string;
    },
  ): Promise<void> {
    await callScribe(
      api.encounter.update({
        EncounterID: consultationId,
        UserID: updates.doctor_user_id,
        StaffMemberID: updates.staff_member_id,
        PatientID: updates.patient_id,
      }),
    );
  }

  async archiveConsultation(consultationId: string): Promise<void> {
    await callScribe(api.encounter.archive({ EncounterID: consultationId }));
  }

  async restoreConsultation(consultationId: string): Promise<void> {
    await callScribe(api.encounter.restore({ EncounterID: consultationId }));
  }

  async deleteConsultation(consultationId: string): Promise<void> {
    await callScribe(api.encounter.delete({ EncounterID: consultationId }));
  }

  async updateConsultationDocument(
    consultationId: string,
    documentId: string,
    payload: DocumentUpdateRequest,
  ): Promise<void> {
    await callScribe(
      api.encounter.updateDocument({
        EncounterID: consultationId,
        DocumentID: documentId,
        Sections: payload.sections?.map((section) => ({
          Code: section.code,
          Content: section.content ?? undefined,
        })),
        Approved: payload.approved,
      }),
    );
  }
}

export const transcriptorApi = new TranscriptorApiClient();
